import { useEffect, useMemo, useState } from "react"
import { Loader2, Save, ShieldCheck } from "lucide-react"
import {
  AuthorizationGrant,
  AuthorizationModule,
  AuthorizationScope,
  getAuthorizationCatalog,
  getAuthorizationScopes,
  getUserGrants,
  replaceUserGrants,
} from "services/authorization"

export function WorkerPermissionsPanel({ userId }: { userId: number }) {
  const [modules, setModules] = useState<AuthorizationModule[]>([])
  const [scopes, setScopes] = useState<Record<string, AuthorizationScope[]>>({})
  const [grants, setGrants] = useState<AuthorizationGrant[]>([])
  const [version, setVersion] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState("")

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.all([getAuthorizationCatalog(), getUserGrants(userId)]).then(async ([catalog, current]) => {
      const configurable = catalog.data.modules.filter(module => module.enabled && module.scope_types.length > 0)
      const loadedScopes = await Promise.all(configurable.map(module => getAuthorizationScopes(module.key)))
      if (!active) return
      setModules(configurable)
      setScopes(Object.fromEntries(configurable.map((module, index) => [module.key, loadedScopes[index].data.results])))
      setGrants(current.data.grants)
      setVersion(current.data.version)
    }).catch(() => setMessage("Impossibile caricare i permessi.")).finally(() => active && setLoading(false))
    return () => { active = false }
  }, [userId])

  const byScope = useMemo(() => new Map(grants.map(grant => [`${grant.module}:${grant.scope_type}:${grant.scope_id}`, grant])), [grants])

  const toggle = (module: string, scope: AuthorizationScope, action: string) => {
    const key = `${module}:${scope.type}:${scope.id}`
    const current = byScope.get(key)
    const actions = new Set(current?.actions || [])
    actions.has(action) ? actions.delete(action) : actions.add(action)
    setGrants(previous => [
      ...previous.filter(grant => `${grant.module}:${grant.scope_type}:${grant.scope_id}` !== key),
      ...(actions.size ? [{ module, scope_type: scope.type, scope_id: scope.id, actions: [...actions].sort() }] : []),
    ])
    setMessage("")
  }

  const save = async () => {
    setSaving(true)
    setMessage("")
    try {
      const response = await replaceUserGrants(userId, version, grants)
      setVersion(response.data.version)
      setMessage("Permessi salvati.")
    } catch (error: any) {
      setMessage(error.response?.data?.code === "authorization_conflict" ? "Configurazione modificata da un altro manager: riapri il pannello." : "Salvataggio non riuscito.")
    } finally { setSaving(false) }
  }

  if (loading) return <div className="flex items-center gap-2 text-xs text-[var(--hexa-text-secondary)]"><Loader2 className="w-4 h-4 animate-spin" />Caricamento permessi…</div>

  return <section className="space-y-3">
    <div className="flex items-center justify-between">
      <h4 className="flex items-center gap-2 text-xs uppercase tracking-wide text-[var(--hexa-text-secondary)]"><ShieldCheck className="w-4 h-4" />Permessi e scope</h4>
      <button type="button" onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-md bg-[var(--hexa-purple)] px-3 py-1.5 text-xs text-white disabled:opacity-50">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Salva
      </button>
    </div>
    {modules.map(module => <div key={module.key} className="rounded-lg bg-[var(--hexa-border-subtle)] p-3">
      <div className="mb-2 text-sm font-semibold capitalize text-[var(--hexa-text-primary)]">{module.key}</div>
      <div className="space-y-2 max-h-44 overflow-auto">
        {(scopes[module.key] || []).map(scope => <div key={`${scope.type}:${scope.id}`} className="border-t border-[var(--hexa-border)] pt-2 first:border-0 first:pt-0">
          <div className="mb-1 text-xs text-[var(--hexa-text-primary)]">{scope.label}</div>
          <div className="flex flex-wrap gap-2">{module.actions.map(action => <label key={action} className="flex items-center gap-1 text-[11px] text-[var(--hexa-text-secondary)]">
            <input type="checkbox" checked={byScope.get(`${module.key}:${scope.type}:${scope.id}`)?.actions.includes(action) || false} onChange={() => toggle(module.key, scope, action)} />{action.split(".").pop()}
          </label>)}</div>
        </div>)}
      </div>
    </div>)}
    {message && <p className="text-xs text-[var(--hexa-text-secondary)]">{message}</p>}
  </section>
}

